import { Decorative } from "@faire/design-tokens";
import { SlateText } from "@faire/retailer-visitor-shared/components/SlateElements/Text/SlateText";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column } from "@faire/web/slate/Layout";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISlateTextElement } from "@faire/web-api--source/indigofair/data/ISlateTextElement";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

export const MAX_IMAGE_WIDTH = 720;
export const MAX_IMAGE_HEIGHT = 876;
// TODO (katherine): get SignUpHeader height more accurately?
export const HEADER_HEIGHT = 68;

interface IVerticalizedImage {
  image: IImage | undefined;
  image_description: Array<ISlateTextElement>;
}

interface IVerticalizedImagesProps {
  images: Record<string, IVerticalizedImage>;
  currentImageKey: string;
  resetLoadedImageKeys?: string[];
  onReloadImages?: () => void;
  loadTimingRef?: React.RefCallback<HTMLElement>;
  timingRefImageKey?: string;
}

export const VerticalizedImages: React.FC<IVerticalizedImagesProps> = ({
  images,
  currentImageKey,
  resetLoadedImageKeys = [],
  onReloadImages,
  loadTimingRef,
  timingRefImageKey,
}) => {
  const isSSR = useIsSSR();
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>(
    Object.keys(images).reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );
  const [visibleImage, setVisibleImage] = useState<string>(currentImageKey);

  useEffect(() => {
    if (resetLoadedImageKeys.length > 0) {
      setLoadedImages((prev) => ({
        ...prev,
        ...Object.fromEntries(
          resetLoadedImageKeys
            .filter((key) => key in prev)
            .map((key) => [key, false])
        ),
      }));
      onReloadImages?.();
    }
  }, [resetLoadedImageKeys, onReloadImages]);

  useEffect(() => {
    if (loadedImages[currentImageKey]) {
      setVisibleImage(currentImageKey);
    }
  }, [currentImageKey, loadedImages]);

  return (
    <ImageContainer>
      {Object.entries(images).map(([imageKey, image]) => (
        <StyledImage
          key={`verticalized-image-${imageKey}`}
          src={optimizeImageUrl(
            {
              url: image?.image?.url,
              width: MAX_IMAGE_WIDTH,
              height: MAX_IMAGE_HEIGHT,
            },
            isSSR,
            { width: MAX_IMAGE_WIDTH, height: MAX_IMAGE_HEIGHT }
          )}
          $isVisible={
            (loadedImages[imageKey] ?? false) && visibleImage === imageKey
          }
          onLoad={() =>
            setLoadedImages((prev) => ({ ...prev, [imageKey]: true }))
          }
          ref={timingRefImageKey === imageKey ? loadTimingRef : undefined}
        />
      ))}
      <ImageOverlay />
      <ImageOverlayText>
        {images[visibleImage]?.image_description.map(
          (text: ISlateTextElement) => (
            <SlateText
              key={`verticalized-image-caption-${text.text}`}
              textElement={text}
            />
          )
        )}
      </ImageOverlayText>
    </ImageContainer>
  );
};

const StyledImage = styled.img<{ $isVisible: boolean }>`
  object-fit: cover;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const ImageContainer = styled.div`
  position: relative;
  background-color: ${Decorative.tint.neutral};
  max-width: ${MAX_IMAGE_WIDTH}px;
  width: 100%;
  height: calc(100vh - ${HEADER_HEIGHT}px);
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    210deg,
    rgba(0, 0, 0, 0) 75%,
    rgba(0, 0, 0, 0.5) 100%
  );
`;

const ImageOverlayText = styled(Column)`
  position: absolute;
  bottom: 0;
  left: 0;
  max-width: 320px;
  padding: 24px;
  & > span {
    text-shadow: 0px 2px 10px rgba(0, 0, 0, 0.15);
  }
`;
